import type { FC } from 'react'
import { useMemo, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Tooltip } from 'antd'
import { InfoCircleOutlined } from '@ant-design/icons'
import { uniqBy } from 'lodash'
import QRCode from 'react-qr-code'
import Barcode from 'react-barcode'

import type { E_CertificateTypes } from '@/enums'
import { useAppStore } from '@/store'
import { formatMoney } from '@/helpers'
import { rqKeys } from '@/react-query-keys'
import { apiRequestNew } from '@/helpers/request'
import { removePendingArchiveQueueItem } from '@/helpers/certificate-archive-offline-queue'
import {
  removePendingRestoreQueueItem,
  upsertPendingRestoreQueueItem,
} from '@/helpers/certificate-restore-offline-queue'
import {
  useCertificatesRest,
  useUpdateCertificateRest,
} from '@/helpers/query/use-certificates-rest'
import { Button, Drawer, Flex, Heading, Input, Modal } from '@/components'
import type { T_CertificateNominalsListRes, T_CertificatesListRes } from '@/types/responses'
import { E_CodeTypes } from '@/types/responses'
import trashSvg from '@/img/icon-trash.svg'
import { CertificateInstruction } from '../../instructions'
import { useArchiveCertificate } from '../use-archive-certificate'
import {
  SC_CodeWrapper,
  SC_TextWrapper,
  SC_DrawerContent,
  SC_RestWrapper,
  SC_RestHeader,
  SC_CertificateIdBadge,
  SC_RestValue,
  SC_Actions,
} from './styled'


interface T_CertificateDetailsDrawerProps {
  certificatesList: T_CertificatesListRes
  selectedCertificateId: number | undefined
  hideCertificateDetailsDrawer: () => void
  certificateType?: E_CertificateTypes;
  isArchivedMode?: boolean
}

export const CertificateDetailsDrawer: FC<T_CertificateDetailsDrawerProps> = ({
  certificatesList,
  selectedCertificateId,
  hideCertificateDetailsDrawer,
  certificateType,
  isArchivedMode = false,
}) => {
  const foundCertificate = certificatesList.find(
    (c) => c.id === selectedCertificateId
  )

  const [ instructionsShown, setInstructionsShown ] = useState(false)

  const certificateNominalsList = useAppStore(s => s.certificateNominalsList)
  const certificateCategoriesList = useAppStore(s => s.certificateCategoriesList)

  const categoryId = useMemo(() => {
    const goodsNominalId = foundCertificate?.goods
    if (!goodsNominalId) return undefined

    const allNominalsFlat: T_CertificateNominalsListRes = certificateNominalsList
      .reduce((acc, group) => acc.concat(group.list), [] as T_CertificateNominalsListRes)

    const uniqueNominals = uniqBy(allNominalsFlat, 'id') as T_CertificateNominalsListRes
    const nominal = uniqueNominals.find(n => n.id === goodsNominalId)
    if (!nominal) return undefined

    const category = certificateCategoriesList.find(c => c.id === nominal.category)

    return category?.id
  }, [ foundCertificate, certificateNominalsList, certificateCategoriesList ])

  const nominalRub = useMemo(() => {
    const goodsNominalId = foundCertificate?.goods
    if (!goodsNominalId) return undefined

    const allNominalsFlat: T_CertificateNominalsListRes = certificateNominalsList
      .reduce((acc, group) => acc.concat(group.list), [] as T_CertificateNominalsListRes)

    const uniqueNominals = uniqBy(allNominalsFlat, 'id') as T_CertificateNominalsListRes
    const nominal = uniqueNominals.find(n => n.id === goodsNominalId)

    if (!nominal) return undefined

    return nominal.price.base.rur / 100
  }, [ foundCertificate, certificateNominalsList ])

  const userData = useAppStore(s => s.userData)
  const { data: certificatesRestList } = useCertificatesRest(userData.id || undefined)
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { mutate: updateRest, isPending: _isUpdatingRest } = useUpdateCertificateRest()

  const restValue = useMemo(() => {
    if (!foundCertificate || !certificatesRestList) return undefined
    const item = certificatesRestList.find(
      r => r.certificateId === foundCertificate.id && r.nominalId === foundCertificate.goods
    )
    return item ? Number(item.amount) : undefined
  }, [ foundCertificate, certificatesRestList ])

  const [ isEditingRest, setIsEditingRest ] = useState(false)
  const [ certificateIdToArchive, setCertificateIdToArchive ] = useState<number>()
  const [ certificateIdToRestore, setCertificateIdToRestore ] = useState<number>()
  const [ restInput, setRestInput ] = useState<string>(
    restValue !== undefined ? String(restValue) : ''
  )
  const { archiveWithOfflineQueue, isMoveCertificateToArchivePending } = useArchiveCertificate()
  const queryClient = useQueryClient()
  const applyOptimisticRestoreMove = (certificateIdToRestore: number) => {
    const previousCertificates = queryClient.getQueryData<T_CertificatesListRes>([ rqKeys.certificatesList ])
    const previousArchived = queryClient.getQueryData<T_CertificatesListRes>([ rqKeys.certificatesList, 'archived' ])
    const certificateToRestore = previousArchived?.find((c) => c.id === certificateIdToRestore)
    if (!certificateToRestore) return { previousCertificates, previousArchived }

    removePendingArchiveQueueItem(userData.id, Number(certificateIdToRestore))
    upsertPendingRestoreQueueItem(userData.id, {
      cert: certificateToRestore,
      movedAt: Date.now(),
    })

    queryClient.setQueryData<T_CertificatesListRes>(
      [ rqKeys.certificatesList ],
      (old) => {
        const next = [ certificateToRestore, ...(old || []) ]
          .filter((c, i, arr) => arr.findIndex((x) => x.id === c.id) === i)
        return next
      }
    )
    queryClient.setQueryData<T_CertificatesListRes>(
      [ rqKeys.certificatesList, 'archived' ],
      (old) => (old || []).filter((c) => c.id !== Number(certificateIdToRestore))
    )

    return { previousCertificates, previousArchived }
  }

  const {
    mutateAsync: restoreCertificateMutationAsync,
    isPending: isRestoreCertificatePending,
  } = useMutation({
    mutationFn: (certificateId: number) => apiRequestNew<any>(
      'GET',
      `/era/gift/certificate/${certificateId}/restore`,
    ),
    onMutate: (certificateId) => {
      const context = applyOptimisticRestoreMove(certificateId)
      void queryClient.cancelQueries({ queryKey: [ rqKeys.certificatesList ] })
      void queryClient.cancelQueries({ queryKey: [ rqKeys.certificatesList, 'archived' ] })
      return context
    },
    onSuccess: (_res, certificateId) => {
      removePendingArchiveQueueItem(userData.id, Number(certificateId))
    },
    onError: (err: any, certificateId, context: any) => {
      const isOfflineNow = typeof navigator !== 'undefined' && navigator.onLine === false
      const isNetworkError = isOfflineNow || err?.code === 'ERR_NETWORK' || !err?.response
      const isAlreadyRestored = Number(err?.response?.status) === 404
      if (isNetworkError) {
        return
      }
      if (isAlreadyRestored) {
        removePendingArchiveQueueItem(userData.id, Number(certificateId))
        return
      }

      removePendingRestoreQueueItem(userData.id, Number(certificateId))
      if (context?.previousCertificates) {
        queryClient.setQueryData([ rqKeys.certificatesList ], context.previousCertificates)
      }
      if (context?.previousArchived) {
        queryClient.setQueryData([ rqKeys.certificatesList, 'archived' ], context.previousArchived)
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: [ rqKeys.certificatesList ], refetchType: 'none' })
      queryClient.invalidateQueries({ queryKey: [ rqKeys.certificatesList, 'archived' ], refetchType: 'none' })
    },
  })

  // Update input when value changes (e.g. initial load) and not editing
  useMemo(() => {
    if (!isEditingRest) {
      setRestInput(restValue !== undefined ? String(restValue) : '')
    }
  }, [ restValue, isEditingRest ])

  const saveRestToLS = () => {
    if (!foundCertificate) return

    const normalized = restInput
      .replace(/,/g, '.')
      .replace(/\s+/g, '')

    if (normalized.trim() === '') {
      setIsEditingRest(false)
      setRestInput(restValue !== undefined ? String(restValue) : '')
      return
    }

    if (/[^0-9+\-.]/.test(normalized)) {
      setIsEditingRest(false)
      setRestInput(restValue !== undefined ? String(restValue) : '')
      return
    }

    const tokens = normalized.match(/[+-]?\d+(?:\.\d+)?/g)

    if (!tokens || tokens.length === 0) {
      setIsEditingRest(false)
      setRestInput(restValue !== undefined ? String(restValue) : '')
      return
    }

    let total = 0

    for (const t of tokens) {
      const n = Number(t)

      if (Number.isNaN(n)) {
        setIsEditingRest(false)
        setRestInput(restValue !== undefined ? String(restValue) : '')
        return
      }

      total += n
    }

    let num: number | undefined = total

    if (num !== undefined && !Number.isNaN(num)) {
      if (num < 0) num = 0
      if (nominalRub !== undefined && num > nominalRub) num = nominalRub
    } else {
      setIsEditingRest(false)
      setRestInput(restValue !== undefined ? String(restValue) : '')
      return
    }

    if (foundCertificate) {
      const ts = Date.now()

      updateRest({
        certificateId: foundCertificate.id,
        nominalId: foundCertificate.goods,
        amount: num,
        userData: userData,
        updatedAtTs: ts,
      }, {
        onSuccess: () => {
          // Данные сохраняются на бэкенде, LocalStorage больше не используется
          setIsEditingRest(false)
        },
        onError: () => {
          setIsEditingRest(false)
          setRestInput(restValue !== undefined ? String(restValue) : '')
        }
      })
    }
  }

  const cancelEditing = () => {
    setIsEditingRest(false)
    setRestInput(restValue !== undefined ? String(restValue) : '')
  }

  const onArchiveConfirm = async () => {
    if (!certificateIdToArchive) return
    await archiveWithOfflineQueue(certificateIdToArchive, foundCertificate)
    setCertificateIdToArchive(undefined)
    hideCertificateDetailsDrawer()
  }

  const onRestoreConfirm = async () => {
    if (!certificateIdToRestore) return
    const isOffline = typeof navigator !== 'undefined' && navigator.onLine === false
    if (isOffline) {
      await queryClient.cancelQueries({ queryKey: [ rqKeys.certificatesList ] })
      await queryClient.cancelQueries({ queryKey: [ rqKeys.certificatesList, 'archived' ] })
      applyOptimisticRestoreMove(certificateIdToRestore)

      setCertificateIdToRestore(undefined)
      hideCertificateDetailsDrawer()
      return
    }

    await restoreCertificateMutationAsync(certificateIdToRestore)
    setCertificateIdToRestore(undefined)
    hideCertificateDetailsDrawer()
  }



  return (
    <Drawer
      onClose={hideCertificateDetailsDrawer}
      isBackdropClosable
      backTitle={instructionsShown ? 'Инструкция по применению' : undefined}
      onBackClick={() => instructionsShown && setInstructionsShown(false)}
    >
      <SC_DrawerContent>
        {instructionsShown ? (
          <CertificateInstruction
            certificateType={certificateType as E_CertificateTypes}
            categoryId={categoryId}
            actionLabel='Назад'
            onActionClick={() => setInstructionsShown(false)}
          />
        ) : <>
          <SC_TextWrapper>
            <Heading level={2} centered>
              Покажите этот {
                foundCertificate?.type === E_CodeTypes.QRCode
                  ? 'QR-код'
                  : 'штрихкод'
              } на кассе
            </Heading>

            <h5>Если на сертификате останутся средства он будет показан на главной странице</h5>
          </SC_TextWrapper>

          {foundCertificate && (
            <SC_RestWrapper>
              <SC_RestHeader>
                {!isArchivedMode ? (
                  <h4 style={{ margin: 0 }}>
                    <Button
                      type='text'
                      size='small'
                      onClick={() => {
                        if (foundCertificate?.id && foundCertificate.id > 0) {
                          setCertificateIdToArchive(foundCertificate.id)
                        }
                      }}
                      style={foundCertificate?.id && foundCertificate.id <= 0
                        ? {
                            color: '#999',
                            cursor: 'not-allowed',
                            opacity: 0.6,
                            padding: 0,
                            height: 'auto',
                            fontSize: 'inherit',
                            lineHeight: 'inherit',
                          }
                        : {
                            color: '#CC2A1F',
                            padding: 0,
                            height: 'auto',
                            fontSize: 'inherit',
                            lineHeight: 'inherit',
                          }}
                    >
                      <Flex align='center' gap='4px'>
                        <span>В архив</span>
                        <img src={trashSvg} alt='' />
                      </Flex>
                    </Button>
                  </h4>
                ) : (
                  <h4 style={{ margin: 0 }}>
                    <Button
                      type='text'
                      size='small'
                      onClick={() => {
                        if (foundCertificate?.id && foundCertificate.id > 0) {
                          setCertificateIdToRestore(foundCertificate.id)
                        }
                      }}
                      style={foundCertificate?.id && foundCertificate.id <= 0
                        ? {
                            color: '#999',
                            cursor: 'not-allowed',
                            opacity: 0.6,
                            padding: 0,
                            height: 'auto',
                            fontSize: 'inherit',
                            lineHeight: 'inherit',
                          }
                        : {
                            color: '#CC2A1F',
                            padding: 0,
                            height: 'auto',
                            fontSize: 'inherit',
                            lineHeight: 'inherit',
                          }}
                    >
                      <span>Восстановить из архива</span>
                    </Button>
                  </h4>
                )}

                {foundCertificate.id > 0 && (
                  <SC_CertificateIdBadge>
                    #{foundCertificate.id}
                  </SC_CertificateIdBadge>
                )}
              </SC_RestHeader>

              <Flex align='center' justify='space-between'>
                <h4>Остаток средств на сертификате:</h4>
                <Flex align='center' gap='8px'>
                  <Tooltip
                    placement='left'
                    title={
                      <div>
                        Так как <strong>мы не получаем от ритейла информацию об остатках</strong> на сертификатах, вы можете записывать его к каждому сертификату в это поле. Либо число, либо остаток минус сколько потрачено.
                        <br /><br />
                        Например, на сертификате с прошлого раза осталось 950 рублей, а вы потратили с него 530 рублей. Тогда можете просто написать <strong>950-530</strong> и в поле ввода получите разницу, чтобы не считать её вручную.
                      </div>
                    }
                  >
                    <InfoCircleOutlined style={{ fontSize: 18, color: '#9E6DEA' }} />
                  </Tooltip>
                </Flex>
              </Flex>

              <Flex align='center' justify='space-between'>
                {!isEditingRest ? <>
                  <SC_RestValue>
                    {restValue !== undefined ? formatMoney(restValue) : 'Нет записи'}
                  </SC_RestValue>

                  <SC_Actions>
                    <Button
                      type='text'
                      onClick={() => setIsEditingRest(true)}
                      icon={<span>✏️</span>}
                      size='small'
                      style={{ fontSize: '14px' }}
                    >
                      Изменить
                    </Button>
                  </SC_Actions>
                </> : (
                  <Flex align='center' gap='8px'>
                    <Input
                      $isWhite
                      name='certificate-rest'
                      value={restInput}
                      onChange={(e) => {
                        const v = e.currentTarget.value
                        const allowed = v.replace(/[^\d+\-.,\s]/g, '')
                        setRestInput(allowed)
                      }}
                      placeholder='Введите сумму или выражение (например: 100-30+20)'
                      style={{ width: '100%', background: 'transparent' }}
                    />

                    <SC_Actions>
                      <Button type='primary' onClick={saveRestToLS}>
                        Ок
                      </Button>

                      <Button ghost onClick={cancelEditing}>
                        Отмена
                      </Button>
                    </SC_Actions>
                  </Flex>
                )}
              </Flex>
            </SC_RestWrapper>
          )}

          <SC_CodeWrapper>
            {foundCertificate?.code && <>
              {foundCertificate?.type === E_CodeTypes.QRCode ? (
                <QRCode value={foundCertificate?.code} />
              ) : foundCertificate?.type === E_CodeTypes.EAN13 ? (
                <Barcode value={foundCertificate?.code} format='EAN13' />
              ) : foundCertificate?.type === E_CodeTypes.CODE128 ? (
                <Barcode value={foundCertificate?.code} format='CODE128' />
              ) : null}
            </>}
          </SC_CodeWrapper>

          <Flex vertical gap={8}>
            <Button type='default' block onClick={() => setInstructionsShown(true)}>
              Подробная инструкция
            </Button>

            <Button type='primary' block onClick={hideCertificateDetailsDrawer}>
              Назад
            </Button>
          </Flex>
        </>}
      </SC_DrawerContent>

      {!!certificateIdToArchive && (
        <Modal
          title='Вы уверены, что хотите архивировать сертификат?'
          open={!!certificateIdToArchive}
          onOk={() => {
            void onArchiveConfirm()
          }}
          onCancel={() => setCertificateIdToArchive(undefined)}
          okText='Архивировать'
          cancelText='Отмена'
          okButtonProps={{ loading: isMoveCertificateToArchivePending }}
        />
      )}
      {!!certificateIdToRestore && (
        <Modal
          title='Вы уверены, что хотите восстановить сертификат из архива?'
          open={!!certificateIdToRestore}
          onOk={() => {
            void onRestoreConfirm()
          }}
          onCancel={() => setCertificateIdToRestore(undefined)}
          okText='Восстановить'
          cancelText='Отмена'
          okButtonProps={{ loading: isRestoreCertificatePending }}
        />
      )}
    </Drawer>
  )
}
